import { OutcomeKind } from './interaction.model';
import { COME_BACK_LATER_OBJECTION_ID } from './objection.model';

/**
 * Single source of truth for "which semantic color does this outcome get" — used by
 * OutcomeChipComponent (chips throughout the app) and the map's building-fill layer, so an
 * outcome reads the same way everywhere rather than each place inventing its own palette.
 * Colour is paired with an icon (in OUTCOME_STYLES) so the meaning survives colourblindness
 * and sunlight — never rely on colour alone. Phase 3 of the design doc allows deferring a
 * DB-driven `outcome_styles` table; this file is the seeded-defaults equivalent until then.
 */
export type ChipColor = 'primary' | 'accent' | 'success' | 'warning' | 'danger';

export const OUTCOME_STYLES: Record<string, { color: ChipColor; icon: string }> = {
  no_answer: { color: 'primary', icon: 'door-closed' },
  objection: { color: 'warning', icon: 'thumbs-down' },
  lead: { color: 'success', icon: 'star' },
  do_not_contact: { color: 'danger', icon: 'ban' },
  other: { color: 'primary', icon: 'more-horizontal' },
};

/**
 * Most objections share the generic 'objection' styling, but "come back later" is a
 * not-now rather than a not-yes, and canvassers need to spot it at a glance on a second
 * pass through a neighbourhood (e.g. later the same day) — so it alone gets an override.
 * Pass the interaction's `objection_id` whenever it's available; omit it only where an
 * objection breakdown genuinely isn't in hand (e.g. a bare `OutcomeKind` with no row to
 * look at).
 */
const OBJECTION_STYLE_OVERRIDES: Record<string, { color: ChipColor; icon: string }> = {
  [COME_BACK_LATER_OBJECTION_ID]: { color: 'accent', icon: 'clock' },
};

export function outcomeStyle(outcome: OutcomeKind | string, objectionId?: string): { color: ChipColor; icon: string } {
  if (outcome === 'objection' && objectionId && OBJECTION_STYLE_OVERRIDES[objectionId]) {
    return OBJECTION_STYLE_OVERRIDES[objectionId];
  }
  return OUTCOME_STYLES[outcome] ?? { color: 'primary', icon: 'circle' };
}

/**
 * The CSS custom property each chip colour actually renders with — mirrors
 * shared/components/chip/chip.component.css's own `.chip-<color> { background-color: ... }`
 * rules exactly (a small, deliberate duplication of *which token name*, not a colour value,
 * so the map can read the same live theme colour via getComputedStyle rather than
 * hardcoding hex — see MapComponent).
 */
export const CHIP_COLOR_BG_VAR: Record<ChipColor, string> = {
  primary: '--bg-tertiary',
  accent: '--bg-accent',
  success: '--bg-success',
  warning: '--bg-warning',
  danger: '--bg-danger',
};
